import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import type { Interview, CompanyStats, DashboardStats } from "../../../types/interview";
import { StatsCard } from "./ui/StatsCard";
import { InterviewsChart } from "./ui/InterviewsChart";
import { CompanyBreakdown } from "./ui/CompanyBreakdown";
import { InterviewsGrid } from "./ui/InterviewsGrid";
import { CompanyGroupedInterviews } from "./ui/CompanyGroupedInterviews";
import { CompanyMultiSelect } from "./ui/CompanyMultiSelect";
import { Calendar, CheckCircle2, Clock, Building2, BarChart3, Grid3X3 } from "lucide-react";

const Dashboard = () => {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "completed" | "pending">("all");
  const [viewMode, setViewMode] = useState<"grid" | "chart">("grid");
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

  useEffect(() => {
    fetchInterviews();
  }, []);

  const fetchInterviews = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "BDSchedules"));
      const interviewsData: Interview[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || "",
          date: data.date?.toDate() || new Date(),
          start: data.start?.toDate() || new Date(),
          end: data.end?.toDate() || new Date(),
          color: data.color || "#0071bd",
          companyName: data.companyName || "",
          panelName: data.panelName || "",
          candidateName: data.candidateName || "",
          candidateEmail: data.candidateEmail || "",
          panelEmail: data.panelEmail || "",
          resourceEmail: data.resourceEmail || "",
          roundDetails: data.roundDetails || "",
          isCompleted: data.isCompleted || false,
          meetingLink: data.meetingLink || "",
          organizer: data.organizer || "",
        };
      });
      setInterviews(interviewsData);
    } catch (error) {
      console.error("Error fetching interviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (): DashboardStats => {
    const totalInterviews = interviews.length;
    const completedInterviews = interviews.filter((i) => i.isCompleted).length;
    const pendingInterviews = totalInterviews - completedInterviews;
    // Use normalized company names for count
    const companySet = new Set(interviews.map(i => i.companyName ? i.companyName.toLowerCase().replace(/\s+/g, "") : "unknown"));
    const companiesCount = companySet.size;

    return {
      totalInterviews,
      completedInterviews,
      pendingInterviews,
      companiesCount,
    };
  };

  // Helper to normalize company names for grouping
  const normalizeCompany = (name: string) => {
    return name ? name.toLowerCase().replace(/\s+/g, "") : "unknown";
  };
  // Helper to check if company should be excluded
  const isExcludedCompany = (name: string) => {
    const n = name.trim().toLowerCase();
    return (
      n === "na" || n === "n/a" || n === "screening" || n === "reserved" || n === "standup" || n === "stand up"
    );
  };
  // Helper to display company name with first letter capital
  const displayCompany = (name: string) => {
    if (!name) return "Unknown";
    const n = name.trim();
    return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
  };

  const getCompanyStats = (): CompanyStats[] => {
    const companyMap = new Map<string, CompanyStats>();

    interviews.forEach((interview) => {
      const rawName = interview.companyName || "Unknown";
      if (isExcludedCompany(rawName)) return;
      const normName = normalizeCompany(rawName);
      const displayName = displayCompany(rawName);
      const existing = companyMap.get(normName) || {
        companyName: displayName,
        total: 0,
        completed: 0,
        pending: 0,
      };

      existing.total += 1;
      if (interview.isCompleted) {
        existing.completed += 1;
      } else {
        existing.pending += 1;
      }

      companyMap.set(normName, existing);
    });

    return Array.from(companyMap.values()).sort((a, b) => b.total - a.total);
  };

  const stats = calculateStats();
  const companyStats = getCompanyStats();
  // All company names sorted by total interviews
  const allCompanyNames = companyStats.map(c => c.companyName);
  // Default: top 10 companies
  useEffect(() => {
    if (selectedCompanies.length === 0 && companyStats.length > 0) {
      setSelectedCompanies(allCompanyNames.slice(0, 10));
    }
  }, [companyStats]);
  // Filter stats for selected companies
  const filteredCompanyStats = companyStats.filter(c => selectedCompanies.includes(c.companyName));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f9fc] p-2 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-4xl font-bold text-[#22223b] mb-2">
            Interviews Dashboard
          </h1>
          <p className="text-[#6c757d]">
            Comprehensive overview of all interviews and their status
          </p>
        </div>

        {/* Stats Cards */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 w-full">
          <StatsCard
            title="Total Interviews"
            value={stats.totalInterviews}
            icon={Calendar}
            variant="primary"
            trend="All time"
          />
          <StatsCard
            title="Completed"
            value={stats.completedInterviews}
            icon={CheckCircle2}
            variant="success"
            trend={`${((stats.completedInterviews / stats.totalInterviews) * 100).toFixed(0)}% completion rate`}
          />
          <StatsCard
            title="Pending"
            value={stats.pendingInterviews}
            icon={Clock}
            variant="warning"
            trend="Awaiting completion"
          />
          <StatsCard
            title="Companies"
            value={stats.companiesCount}
            icon={Building2}
            variant="default"
            trend="Active clients"
          />
        </div>

        {/* Filter Section */}
  <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
          <label htmlFor="company-select" className="text-sm font-semibold text-[#22223b]">Filter by Company:</label>
          <CompanyMultiSelect
            companies={allCompanyNames}
            selected={selectedCompanies}
            onChange={setSelectedCompanies}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
          <div className="lg:col-span-2 w-full">
            <div className="w-full overflow-x-auto">
              <InterviewsChart data={filteredCompanyStats} type="bar" />
            </div>
          </div>
          <div className="w-full">
            <CompanyBreakdown companies={filteredCompanyStats.slice(0, 5)} />
          </div>
        </div>

        {/* Interviews List with Filters */}
  <div className="space-y-4 w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 w-full">
            <div className="flex flex-wrap gap-2 w-full">
              <button
                className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-sm ${viewMode === "grid" ? "bg-[#0071bd] text-white" : "bg-gray-100 text-[#0071bd]"}`}
                onClick={() => setViewMode("grid")}
              >
                <Grid3X3 className="w-4 h-4 mr-2" />
                Grid
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-sm ${viewMode === "chart" ? "bg-[#0071bd] text-white" : "bg-gray-100 text-[#0071bd]"}`}
                onClick={() => setViewMode("chart")}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Chart
              </button>
            </div>
            <div className="flex flex-wrap gap-2 w-full">
              <button
                className={`px-4 py-2 rounded-lg font-semibold shadow-sm ${filter === "all" ? "bg-[#0071bd] text-white" : "bg-gray-100 text-[#0071bd]"}`}
                onClick={() => setFilter("all")}
              >All</button>
              <button
                className={`px-4 py-2 rounded-lg font-semibold shadow-sm ${filter === "completed" ? "bg-[#28a745] text-white" : "bg-gray-100 text-[#28a745]"}`}
                onClick={() => setFilter("completed")}
              >Completed</button>
              <button
                className={`px-4 py-2 rounded-lg font-semibold shadow-sm ${filter === "pending" ? "bg-[#ff9800] text-white" : "bg-gray-100 text-[#ff9800]"}`}
                onClick={() => setFilter("pending")}
              >Pending</button>
            </div>
          </div>

          {viewMode === "grid" ? (
            <CompanyGroupedInterviews
              interviews={interviews.filter(i =>
                selectedCompanies.includes(displayCompany(i.companyName)) && !isExcludedCompany(i.companyName)
              )}
              filter={filter}
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 w-full">
              <div className="w-full overflow-x-auto">
                <InterviewsChart data={filteredCompanyStats} type="bar" />
              </div>
              <div className="w-full overflow-x-auto">
                <InterviewsChart data={filteredCompanyStats} type="pie" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
